/**
 * Date conversion utilities
 * Handles conversion of date strings from backend format to display format
 */

/**
 * Format date from "12/24/2025, 19:30:00" to a more readable format
 * @param dateString - Date string in format "MM/DD/YYYY, HH:MM:SS"
 * @returns Object with formatted date and time strings
 */
export const formatEventDate = (
    dateString: string
): { date: string; time: string } => {
    try {
        // Parse the date string format: "12/24/2025, 19:30:00"
        const [datePart, timePart] = dateString.split(', ')
        if (!datePart || !timePart) {
            throw new Error('Invalid date format')
        }

        // Parse MM/DD/YYYY format
        const [month, day, year] = datePart.split('/').map(Number)
        
        // Parse HH:MM:SS format, handling AM/PM
        let hours: number
        let minutes: number
        
        // Check if timePart contains AM/PM
        const isPM = timePart.toUpperCase().includes('PM')
        const isAM = timePart.toUpperCase().includes('AM')
        
        // Remove AM/PM suffix and parse time
        const timeWithoutPeriod = timePart.replace(/\s*(AM|PM)\s*/i, '')
        const [hoursStr, minutesStr] = timeWithoutPeriod.split(':')
        
        hours = parseInt(hoursStr, 10)
        minutes = parseInt(minutesStr, 10)
        
        // Convert to 24-hour format if PM
        if (isPM && hours !== 12) {
            hours += 12
        } else if (isAM && hours === 12) {
            hours = 0
        }

        // Create date object (month is 0-indexed in JavaScript Date)
        const date = new Date(year, month - 1, day, hours, minutes)

        // Format date as "24 December"
        const dateStr = date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
        })

        // Format time as "19:30h"
        const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}h`

        return { date: dateStr, time: timeStr }
    } catch {
        // Fallback to original string if parsing fails
        const parts = dateString.split(', ')
        return {
            date: parts[0] || dateString,
            time: parts[1] ? `${parts[1].split(':').slice(0, 2).join(':')}h` : '',
        }
    }
}

